import { type ShopfrontDispatch } from "../../../_Bootstrap/hooks";
import { store } from "../../../_Bootstrap/RoutingAndStore";
import { showMissingPermissions } from "../../../Actions/CustomerDisplay/CustomerDisplayActions";
import PopupManager from "../../../Common/Popups/PopupManager";
import UUID from "../../../Utilities/Generic/UUID";
import { getLogLevel, LogLevel } from "../../../Utilities/Platform/Logging";
import { getOption } from "../../../Utilities/Platform/Options/Options";
import { DISPLAY_FROM_SHOPFRONT, DISPLAY_TO_SHOPFRONT } from "../DisplayEvents";
import BaseDisplayDriver, { DisplayDriverEvents } from "./BaseDisplayDriver";

const CONNECTED_INTERVAL = 1000; // 1 second in milliseconds
const CONNECTING_INTERVAL = 10 * 1000; // 10 seconds in milliseconds

export default class PopupDisplayDriver extends BaseDisplayDriver {
    protected broadcastChannel: BroadcastChannel;
    protected heartbeatInterval: ReturnType<typeof setInterval> | undefined;
    protected heartbeatRequest: null | string = null;
    protected heartbeatFailed = 0;
    protected connected = false;
    protected closedEmitted = false;

    constructor() {
        super();

        this.onMessage = this.onMessage.bind(this);
        this.checkIsOpen = this.checkIsOpen.bind(this);

        this.broadcastChannel = new BroadcastChannel("CustomerDisplayChannel");
        this.broadcastChannel.addEventListener("message", this.onMessage);
    }

    public async init(): Promise<void> {
        this.heartbeatInterval = setInterval(this.checkIsOpen, CONNECTING_INTERVAL);
        this.checkIsOpen();
    }

    protected checkIsOpen(): void {
        // Check to see if the previous request didn't get handled
        if(this.heartbeatRequest !== null) {
            if(this.connected) {
                this.heartbeatFailed++;

                // Mark as failed when three
                if(this.heartbeatFailed > 3) {
                    // Mark the driver as not connected
                    this.connected = false;
                    this.emit(DisplayDriverEvents.DRIVER_CLOSE);

                    // Set the interval every 10 seconds
                    clearInterval(this.heartbeatInterval);
                    this.heartbeatInterval = setInterval(this.checkIsOpen, CONNECTING_INTERVAL);

                    this.heartbeatFailed = 0;
                }
            } else if(!this.closedEmitted) {
                // Ensure the close is emitted only once (to prevent spamming driver closed events)
                this.closedEmitted = true;
                this.emit(DisplayDriverEvents.DRIVER_CLOSE);
            }
        }

        // Send the new heartbeat request
        this.heartbeatRequest = UUID.generate();
        void this.sendMessage(DISPLAY_FROM_SHOPFRONT.DISPLAY_HEARTBEAT_REQUEST, this.heartbeatRequest);
    }

    protected onMessage({ data }: MessageEvent): void {
        if(typeof data !== "object") {
            return;
        }

        if(data === null) {
            return;
        }

        const { type, body } = data as Record<string, unknown>;

        if(typeof type !== "string") {
            return;
        }

        if(!(type in DISPLAY_TO_SHOPFRONT)) {
            return;
        }

        // Check to see if the message is the current heartbeat response
        if(type === DISPLAY_TO_SHOPFRONT.DISPLAY_HEARTBEAT_RESPONSE) {
            if(this.heartbeatRequest === body) {
                // Clear the current request
                this.heartbeatRequest = null;

                // Check to see if the driver wasn't connected prior
                if(!this.connected) {
                    this.closedEmitted = false;
                    this.connected = true;
                    this.emit(DisplayDriverEvents.DRIVER_OPEN);

                    // Set the interval every second
                    clearInterval(this.heartbeatInterval);
                    this.heartbeatInterval = setInterval(this.checkIsOpen, CONNECTED_INTERVAL);
                }
            }

            return;
        }

        this.emit(DisplayDriverEvents.MESSAGE, {
            type: type as DISPLAY_TO_SHOPFRONT,
            body,
        });
    }

    public async close(): Promise<void> {
        await this.sendMessage(
            DISPLAY_FROM_SHOPFRONT.DISPLAY_CLOSE,
            undefined
        );
    }

    public async open(): Promise<boolean> {
        let screen: ScreenDetailed | null = null;

        if(typeof window.getScreenDetails === "function") {
            try {
                const screenDetails = await window.getScreenDetails();

                if(screenDetails.screens.length <= 1) {
                    PopupManager.error(
                        "Unable to open the customer display due to only one monitor being detected"
                    );

                    return false;
                }

                screen = screenDetails.currentScreen;
            } catch(err) {
                if(!(err instanceof DOMException)) {
                    throw err;
                }

                if(err.name !== "NotAllowedError") {
                    throw err;
                }

                // Show the help article on how to fix missing permissions
                (store.dispatch as ShopfrontDispatch)(showMissingPermissions());

                return false;
            }
        }

        if(getLogLevel() === LogLevel.VERBOSE) {
            console.log("Attempting to open the customer display using a new window");
        }

        let x: number;
        let y: number;
        let w: number;
        let h: number;

        if(screen) {
            x = screen.left;
            y = screen.top;
            w = screen.width;
            h = screen.height;
        } else {
            x = getOption("register_customer_display_left");
            y = getOption("register_customer_display_top");

            if(!x) x = 0;

            if(!y) y = 0;

            w = 1000;
            h = 1000;
        }

        // Workaround improper handling of (0,0) locations crbug.com/1392876
        if(x === 0 && y === 0) {
            x++;
            w--;
        }

        const popup = window.open(
            "/sell/display",
            "CustomerDisplayScreen",
            `left=${x},top=${y},width=${w},height=${h}`
        );

        if(!popup) {
            PopupManager.error("Unable to open the customer display due to the popup being blocked");

            return false;
        }

        popup.moveTo(x, y);

        // Mark the display as open
        this.connected = true;
        this.emit(DisplayDriverEvents.DRIVER_OPEN);

        // Set the interval every second
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = setInterval(this.checkIsOpen, CONNECTED_INTERVAL);

        return true;
    }

    public async sendMessage(type: DISPLAY_FROM_SHOPFRONT, body: unknown): Promise<void> {
        this.broadcastChannel.postMessage({
            type,
            body,
        });
    }
}
